import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '~/components/Layout';
import { emailRegex } from '@src/components/FormFields/constants';
import { FormFields } from '~/components/FormFields';
import { Controller, useForm } from 'react-hook-form';
import { Form, Icon, Input, Dropdown } from 'semantic-ui-react';
import { useParams, useSearchParams } from 'react-router-dom';
import notify from '~/components/Notify';
import { UsersQuery } from '~/QueryHooks/UsersQuery';
import { useAuth } from '~/routes/components/AuthProvider';
import FileUpload from '~/components/FileUpload'
import { RouteTypes } from '~/routes/constants';
import { StyledImageWrapper, StyledImageContainer } from './style';
import UsersService from '~/api/services/users';
import { handleErrors, parseDate } from '~/utils/helpers';
import dayjs from 'dayjs';
import { DateInput } from 'semantic-ui-calendar-react';

const defaultImage = '/assets/default.png';

const filterKeys = ['search', 'agisId', 'email', 'birthDate', 'languageId', 'type'] as const

const CreateEditUser: React.FC = () => {
  const { t } = useTranslation();
  const { systemData: { languages, chEnv, logged: { languageId } } } = useAuth();
  const params = useParams()
  const [searchParams] = useSearchParams()
  const isSwissDairy = chEnv === 1

  const buildCloseUrl = () => {
    const urlParams = new URLSearchParams()
    filterKeys.forEach(key => {
      const value = searchParams.get(key)
      if (value) urlParams.set(key, value)
    })
    const query = urlParams.toString()
    const base = isSwissDairy ? RouteTypes.DAIRY_FACTORY_MANAGER_USERS : RouteTypes.DAIRY_FACTORY_FARMER_USERS
    return query ? `${base}?${query}` : base
  }
  const languageLocal = languages.find(language => language.id === languageId)

  const { handleSubmit, control, setValue, watch, reset } = useForm({
    mode: 'onSubmit'
  });

  useEffect(() => {
    fetchFile()
  }, [])

  async function fetchFile() {
    if (params.id) {
      try {
        const response = await UsersService.getUserFile(params.id)
        setValue('photo', response.data)
      } catch (error) {
        handleErrors(error)
      }
    }
  }

  function onSuccess() {
    if (params.id) {
      notify(t('User successfully updated'), 'success');
    } else {
      notify(t('User successfully created'), 'success');
    }
    refetchEditUserData();
  }

  async function onGetUserDataSuccess(data) {
    if (params.id) {
      const response = await UsersService.getUserFile(params.id)
      setValue('photo', response.data)
      reset({ ...data, photo: response.data, birthDate: data?.birthDate ? dayjs(data?.birthDate).format('DD.MM.YYYY') : '' });
    }
  }

  const {
    data: usersOptions,
    isLoading: isBusyUsersOptions
  } = UsersQuery.getUserOptionsData();

  const {
    isLoading: isBusyUserData,
    isFetching: isFetching,
    data: userData,
    refetch: refetchEditUserData
  } = UsersQuery.getEditUser(onGetUserDataSuccess, params.id);

  const photoFieldUrl = watch('photo')
  const watchPhoneCodeField = watch('phonePrefix')
  const wathcMobileCodeField = watch('mobilePrefix')
  const mobileNumber = watch('mobileNumber')
  const phoneNumber = watch('phoneNumber')

  const { isLoading: isUserStoreLoading, mutateAsync: storeUser } =
    UsersQuery.storeUser(onSuccess);

  const { isLoading: isUserEditLoading, mutateAsync: editUser } =
    UsersQuery.editUser(onSuccess);

  const onSubmit = async (data) => {
    if (params.farmerId) {
      data.farmer_id = params.farmerId;
    }

    if (data?.birthDate) {
      data.birthDate = parseDate(data.birthDate)
    }
    try {
      if (!params.id) {
        await storeUser(data);
      } else {
        await editUser(data);
      }
    } catch (err) {
      console.log('onSubmit error', err);
    }
  };

  function renderUserForm() {
    const maxDate = dayjs().subtract(18, 'year').format('DD.MM.YYYY')
    const minDate = dayjs().subtract(90, 'year').format('DD.MM.YYYY')
    return <>
      <Form.Field>
        {photoFieldUrl && <StyledImageWrapper>
          <img src={photoFieldUrl ? photoFieldUrl : defaultImage}></img>
          <StyledImageContainer />
          {photoFieldUrl && <Icon
            circular
            onClick={(e) => { e.preventDefault(); setValue('photo', null) }}
            name='trash'
            className='trashCan'
          />
          }
        </StyledImageWrapper>
        }
        {!photoFieldUrl && <FileUpload
          encodeFiles
          maxWidthDropZone={'100%'}
          onChange={(value) => setValue('photo', value)}
          accept={{
            'image/bmp': ['.bmp'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png']
          }}
        />
        }
      </Form.Field>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          label={t('First name')}
          rules={{
            required: {
              value: true,
              message: t('Please enter first name')
            },
            maxLength: {
              value: 100,
              message: t('Max char is 100')
            }
          }}
          style={{ marginBottom: 10 }}
          name={'firstName'}
          placeholder={t('First name')}
        />
        <FormFields.Input
          control={control}
          label={t('Last name')}
          rules={{
            required: {
              value: true,
              message: t('Please enter last name')
            },
            maxLength: {
              value: 100,
              message: t('Max char is 100')
            }
          }}
          style={{ marginBottom: 10 }}
          name={'lastName'}
          placeholder={t('Last name')}
        />
      </Form.Group>
      {!(params.id && userData) && <FormFields.Input
        name='email'
        type='text'
        label={t('Email')}
        rules={{
          required: {
            value: true,
            message: t('Please enter email')
          },
          pattern: {
            value: emailRegex,
            message: t('Please enter valid email')
          }
        }}
        style={{ marginBottom: 10 }}
        autoComplete='off'
        placeholder={t('Email')}
        control={control}
      />
      }
      <Form.Group widths={'equal'}>
        <FormFields.Select
          options={languages?.map(language => ({ value: language?.id, text: language?.label }))}
          selection
          style={{ marginBottom: 10 }}
          selectOnBlur={false}
          placeholder={t('Select Language')}
          control={control}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name={'world'} style={{ fontSize: '1.2em' }} />
            <div>{t('Language')}</div>
          </label>}
          name={'languageId'}
        />

        <Controller
          control={control}
          name='birthDate'
          rules={{
            validate: (value) => {
              if (!value) {
                return true
              }

              const age = dayjs().diff(dayjs(value, 'DD.MM.YYYY'), 'year')
              if (age < 18) return t('You must be at least 18 years old')
              if (age > 90) return t('Date of birth is not valid')
              return true
            }
          }}
          render={({ field, fieldState }) => (
            <Form.Field error={Boolean(fieldState.error)}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 3 }}>
                <Icon name={'calendar alternate outline'} style={{ fontSize: '1.2em' }} />
                <div>{t('Date of birth')}</div>
              </label>
              <DateInput
                name='birthDate'
                placeholder={t('Date')}
                value={field.value || ''}
                clearable
                dateFormat='DD.MM.YYYY'
                iconPosition='left'
                closable
                localization={languageLocal?.code}
                minDate={minDate}
                maxDate={maxDate}
                initialDate={maxDate}
                autoComplete='new-password'
                onChange={(e, { value }) => field.onChange(value)}
              />
            </Form.Field>
          )}
        />

      </Form.Group>
      <Form.Group widths={'equal'}>
        <Form.Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name={'text telephone'} style={{ fontSize: '1.2em' }} />
            <div>{t('Phone number')}</div>
          </label>
          <Input
            value={phoneNumber || ''}
            label={<Dropdown
              options={usersOptions?.countries.map(countryCode => ({ value: countryCode.id, text: countryCode.phoneCode, flag: countryCode.iso }))}
              selectOnBlur={false}
              value={parseInt(watchPhoneCodeField) || ''}
              scrolling
              basic
              placeholder={t('Code')}
              clearable
              onChange={(e, data) => setValue('phonePrefix', data.value)}
              name={'phonePrefix'}
            />}
            onChange={(e, data) => {
              setValue('phoneNumber', data.value)
            }}
            labelPosition='left'
            style={{ marginBottom: 10 }}
            placeholder={watchPhoneCodeField ? t('Phone number') : t('Please select phone code')}
          />
        </Form.Field>
        <Form.Field style={{ paddingLeft: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name={'mobile alternate'} style={{ fontSize: '1.2em' }} />
            <div>{t('Mobile number')}</div>
          </label>
          <Input
            value={mobileNumber || ''}
            label={<Dropdown
              scrolling
              basic
              clearable
              options={usersOptions?.countries.map(countryCode => ({ value: countryCode.id, text: countryCode.phoneCode, flag: countryCode.iso }))}
              selectOnBlur={false}
              value={parseInt(wathcMobileCodeField) || ''}
              onChange={(e, data) => setValue('mobilePrefix', data.value)}
              placeholder={t('Code')}
              name={'mobilePrefix'}
            />}
            onChange={(e, data) => setValue('mobileNumber', data.value)}
            labelPosition='left'
            style={{ marginBottom: 10 }}
            type={'number'}
            placeholder={wathcMobileCodeField ? t('Mobile number') : t('Please select mobile code')}
            name={'mobileNumber'}
          />
        </Form.Field>
      </Form.Group>
    </>
  }

  const email = userData?.email
  const firstName = userData?.firstName
  const lastName = userData?.lastName

  return <Layout
    segmented
    title={!isFetching && params.id && userData ? `${firstName} ${lastName} (${email})` : t('Create user')}
    close={buildCloseUrl()}
    isBusy={isBusyUsersOptions || isBusyUserData || isUserStoreLoading || isUserEditLoading}
    action={params.id && userData ? t('Update') : t('Create')}
    onSubmit={handleSubmit(onSubmit)}
  >
    {!isFetching && renderUserForm()}

  </Layout>
}
export default CreateEditUser
